import { useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import { EDeviceTypes } from "../../../../shared/oversight-core/enums/device-types";
import AppSelect, {
  Option,
} from "../../../../shared/oversight-core/ui-elements/app-select/app-select";
import AppInput from "../../../../shared/oversight-core/ui-elements/input/app-input";
import MaterialIcon from "../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import ModalContainer, {
  ModelContainerProps,
} from "../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import { getDeviceLabel } from "../../../../shared/oversight-core/utils/getDeviceType";
import { ISimplifiedDevice } from "../simplified-device-energy-card/simplified-device-energy-card";

const deviceOptions: Option[] = [
  { value: EDeviceTypes.TV, label: getDeviceLabel(EDeviceTypes.TV, true) },
  {
    value: EDeviceTypes.BULB,
    label: getDeviceLabel(EDeviceTypes.BULB, true),
  },
  {
    value: EDeviceTypes.COMPUTER,
    label: getDeviceLabel(EDeviceTypes.COMPUTER, true),
  },
  {
    value: EDeviceTypes.AIR_CONDITIONER,
    label: getDeviceLabel(EDeviceTypes.AIR_CONDITIONER, true),
  },
  {
    value: EDeviceTypes.RICE_COOKER,
    label: getDeviceLabel(EDeviceTypes.RICE_COOKER, true),
  },
  {
    value: EDeviceTypes.REFRIGERATOR,
    label: getDeviceLabel(EDeviceTypes.REFRIGERATOR, true),
  },
  {
    value: EDeviceTypes.WASHING_MACHINE,
    label: getDeviceLabel(EDeviceTypes.WASHING_MACHINE, true),
  },
  {
    value: EDeviceTypes.GENERAL,
    label: (
      <Row key={uuidv4()} className="align-items-center flex-nowrap">
        <Col className={`col-auto pe-0`} key={uuidv4()}>
          <div className={`p-1 ${EDeviceTypes.GENERAL} rounded`}>
            <MaterialIcon icon={"dashboard"} size={19} />
          </div>
        </Col>
        <Col key={uuidv4()}>Other</Col>
      </Row>
    ),
  },
];

interface IFormInput {
  deviceType: Option | [];
  deviceName: string;
}

const defaultFormValues: IFormInput = {
  deviceType: [],
  deviceName: "",
};

interface IProps extends ModelContainerProps {
  devices: ISimplifiedDevice[];
  onAddDevice: (deviceType: EDeviceTypes, deviceName: string) => void;
}

const AddDeviceModal = (props: IProps) => {
  const { show, devices, onAddDevice, ...rest } = props;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
  } = useForm<IFormInput>({
    defaultValues: { ...defaultFormValues },
  });

  useEffect(() => {
    if (show) {
      reset({ ...defaultFormValues });
    }
  }, [show]);

  const onSubmit = (data: IFormInput) => {
    if (data.deviceType) {
      onAddDevice(
        (data.deviceType as Option).value as EDeviceTypes,
        data.deviceName
      );
    }
  };

  return (
    <ModalContainer show={show} {...rest} onConfirm={handleSubmit(onSubmit)}>
      <>
        <Row>
          <Col>
            <AppInput
              label="Device Name"
              placeholder="Device Name"
              name="deviceName"
              errors={errors}
              register={register("deviceName", {
                required: "Please enter the device name",
                validate: (value) =>
                  !devices.map((device) => device.deviceName).includes(value) ||
                  "This device name already exists",
              })}
              isRequired={false}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <AppSelect
              className="mt-4"
              placeholder="Device Type"
              selectedValue={undefined}
              options={deviceOptions}
              label="Device Type"
              control={control}
              name="deviceType"
              errors={errors}
              register={register("deviceType", {
                required: "Please select the device type",
              })}
              menuHeight="115px"
            />
          </Col>
        </Row>
      </>
    </ModalContainer>
  );
};

export default AddDeviceModal;
